import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { z } from "zod/v4";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const copyId = (id: string) => {
  navigator.clipboard.writeText(id);
};

export const truncateId = (id: string) => {
  return id.slice(0, 12).concat("...");
};

export const truncateIdFirstLast = (id: number | string | null | undefined) => {
  if (id == null) {
    return "-";
  }
  const idStr = id.toString();
  if (idStr.length <= 10) {
    return idStr;
  }
  return `${idStr.slice(0, 5)}...${idStr.slice(-5)}`;
};

export const validateName = (name: string): string | null => {
  if (!name.trim()) {
    return "Account name is required";
  }

  if (name.length > 255) {
    return "Account name must be less than 255 characters";
  }

  if (name.includes("\0") || /[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/.test(name)) {
    return "Account name contains invalid characters";
  }

  return null;
};

// Last Login Helpers

export const formatLastLogin = (lastLoginAt: string | undefined): string => {
  if (!lastLoginAt) return "-";

  return new Date(lastLoginAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const isLastLoginOverTwoWeeks = (lastLoginAt?: string): boolean => {
  if (!lastLoginAt) return false;

  const lastLoginTime = Date.parse(lastLoginAt);

  if (isNaN(lastLoginTime)) return false;

  const now = Date.now();
  const twoWeeksMs = 14 * 24 * 60 * 60 * 1000;
  
  return lastLoginTime < now - twoWeeksMs;
};

// Zod transformation helpers
export const transformNumber = (val: string) => Number(val);

export const transformDate = (val: string) => new Date(val);

export const transformYear = (val: string) => {
  const year = Number(val);
  const date = new Date(year, 0, 1);
  return date.getFullYear();
};

// Zod validation helpers
export const validateLatitude = (val: string, ctx: z.RefinementCtx) => {
  try {
    const parsed = Number.parseInt(String(val));
    if (parsed < -90 || parsed > 90) {
      ctx.issues.push({
        code: "custom",
        message: "Latitude must be between -90 and 90",
        input: val,
      });
      return z.NEVER;
    }
    return parsed;
  } catch (e) {
    ctx.issues.push({
      code: "custom",
      message: "Not a number",
      input: val,
    });
    return z.NEVER;
  }
};

export const validateLongitude = (val: string, ctx: z.RefinementCtx) => {
  try {
    const parsed = Number.parseInt(String(val));
    if (parsed < -180 || parsed > 180) {
      ctx.issues.push({
        code: "custom",
        message: "Longitude must be between -180 and 180",
        input: val,
      });
      return z.NEVER;
    }
    return parsed;
  } catch (e) {
    ctx.issues.push({
      code: "custom",
      message: "Not a number",
      input: val,
    });
    return z.NEVER;
  }
};

// Zod schema helpers
export const createNumberSchema = () => 
  z.optional(z.string().transform(transformNumber));

export const createDateSchema = () => 
  z.optional(z.string().transform(transformDate));

export const createYearSchema = () => 
  z.optional(
    z
      .string()
      .transform(transformYear)
      .pipe(z.number().max(new Date().getFullYear())),
  );

export const createLatitudeSchema = () => 
  z.optional(z.string().transform(validateLatitude));

export const createLongitudeSchema = () => 
  z.optional(z.string().transform(validateLongitude));

export const createUrlSchema = () => 
  z.optional(z.url());

export const createUuidSchema = () => 
  z.optional(z.uuid());

export const createStringSchema = () => 
  z.optional(z.string());
